import React, { useCallback, useEffect } from 'react';
import { deepGet, deepSet, isCurrencyField } from '../utils/dynamicFormUtils';
import DynamicField from './DynamicField';

/**
 * Renders a dynamic form from schema (only enabled fields).
 * Chỉ gọi lại API getRequiredFieldsGsn khi user đổi field có field.refresh === true:
 * - AirWallex: dùng đúng field.refresh từ API.
 * - Wise: refreshRequirementsOnChange được map thành field.refresh trong normalizer.
 * @param {{
 *   schema: { condition?: object, fields: import('../utils/fieldSchemaTypes').FieldSchema[] } | null,
 *   formData: Object,
 *   setFieldValue: (path: string, value: *) => void,
 *   errors: Record<string, string>,
 *   triggerRefresh: (condition: object) => void,
 *   loading?: boolean,
 *   refreshLoading?: boolean,
 *   refreshPending?: boolean,
 *   disabled?: boolean,
 *   loadingActions?: { showLoadingSpiner: function, hideLoadingSpiner: function },
 *   currencyISO?: string,
 * }}
 */
function DynamicFormEngine({
  schema,
  formData,
  setFieldValue,
  errors,
  triggerRefresh,
  loading = false,
  refreshLoading = false,
  refreshPending = false,
  disabled = false,
  loadingActions,
  currencyISO,
}) {
  const fields = schema?.fields ?? [];
  const enabledFields = fields.filter((f) => f.enabled === true);

  // Chỉ bật spinner toàn màn hình khi load lần đầu; khi refresh không bật để tránh giật
  useEffect(() => {
    if (!loadingActions) return;
    if (loading) {
      loadingActions.showLoadingSpiner();
    } else {
      loadingActions.hideLoadingSpiner();
    }
    return () => {
      if (loading) loadingActions.hideLoadingSpiner();
    };
  }, [loading, loadingActions]);

  const handleChange = useCallback(
    (path, value, needsRefresh) => {
      setFieldValue(path, value);
      if (needsRefresh) {
        const nextFormData = JSON.parse(JSON.stringify(formData));
        deepSet(nextFormData, path, value);
        triggerRefresh(nextFormData, schema?.fields ?? [], schema);
      }
    },
    [formData, setFieldValue, triggerRefresh, schema]
  );

  if (!schema || enabledFields.length === 0) {
    return null;
  }

  const isRefreshing = refreshLoading || refreshPending;

  return (
    <div className="dynamic-form-engine">
      {enabledFields.map((fieldSchema) => {
        const fixedCurrency = !!(currencyISO && isCurrencyField(fieldSchema));
        return (
          <DynamicField
            key={fieldSchema.path}
            fieldSchema={fieldSchema}
            value={fixedCurrency ? currencyISO : deepGet(formData, fieldSchema.path)}
            error={errors[fieldSchema.path]}
            formData={formData}
            onChange={(value) =>
              handleChange(
                fieldSchema.path,
                value,
                Boolean(fieldSchema.field?.refresh)
              )
            }
            disabled={disabled || isRefreshing || fixedCurrency}
            fixedCurrency={fixedCurrency}
          />
        );
      })}
    </div>
  );
}

export default DynamicFormEngine;
